import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  DEFAULTS, FACTOR_LABELS, HORIZONS, clearGridCache, describeScan, fetchOptions, hitsByGroup,
  num, outcomeSummary, outcomeVerdict, pct, runScan, topHits,
} from '../utils/planetaryAnalog';
import { FAMILY_CODES, baselineShares, matchType } from '../utils/planetaryAnalogOverlay';

const ALL_FACTORS = Object.keys(FACTOR_LABELS);

/**
 * The planetary analog scanner. ADMIN ONLY, and research rather than product.
 *
 * <h2>⚠ THE PANEL LEADS WITH THE CAVEAT, not with the score</h2>
 *
 * The headline number here is bits of improbability, which is a measure of COINCIDENCE and says
 * nothing about what happens next. Every other study in this repository that produced a
 * confident-looking figure turned out to have no directional edge once it was measured against
 * its own base rate, and this one measured at t = 0.09 on SPY over sixty analogs. A panel that
 * put a big number at the top and the base rate in a footnote would be inviting exactly the
 * reading the measurement refutes.
 *
 * <h2>The outcome row is computed HERE so it moves with the parameters</h2>
 *
 * A reader changing the lookback or the factor set should see the edge move — or, as it
 * happens, not move. Burying it in the server's response would make it a thing you read once.
 */
const PlanetaryAnalogDialog = ({
  open, onClose, isAdmin, token, symbol, onFocusDate, onPaint, onClearMarks,
}) => {
  const [targetDate, setTargetDate] = useState('');
  const [lookbackYears, setLookbackYears] = useState(DEFAULTS.lookbackYears);
  const [limit, setLimit] = useState(DEFAULTS.limit);
  const [minScore, setMinScore] = useState(DEFAULTS.minConfidenceScore);
  const [minAge, setMinAge] = useState(DEFAULTS.minAnalogAgeDays);
  const [useSymbol, setUseSymbol] = useState(true);
  const [factors, setFactors] = useState(ALL_FACTORS);
  const [serverFactors, setServerFactors] = useState(null);

  const [state, setState] = useState('IDLE');
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [paintNote, setPaintNote] = useState('');

  // Supersession, as the Deep Pivot dialog does it: a slow scan must not land its results over
  // a newer one's. A ref rather than state, because it is read inside an async closure.
  const scanId = useRef(0);

  // ⚠ THE CALLBACK GOES THROUGH A REF. A parent that passes an inline arrow -- which is how
  // every dialog in this app is wired -- makes a new function every render, so naming it in a
  // dependency array would rebuild the handler on every render.
  const focusRef = useRef(onFocusDate);
  focusRef.current = onFocusDate;
  // Same reason as `focusRef`: the parent passes inline arrows, so naming these in a dependency
  // array would rebuild `run` on every render of the app.
  const paintRef = useRef(onPaint);
  paintRef.current = onPaint;
  const clearRef = useRef(onClearMarks);
  clearRef.current = onClearMarks;

  useEffect(() => {
    if (!open || !isAdmin || !token || serverFactors) return;
    let cancelled = false;
    // The factor list is SERVED rather than hardcoded, so a group added in Java appears here
    // with no second edit. The local FACTOR_LABELS is for wording only.
    fetchOptions(token)
      .then((opts) => {
        if (cancelled) return;
        const ids = (opts.factors || []).map((f) => f.id);
        if (ids.length) {
          setServerFactors(ids);
          setFactors(ids);
        }
      })
      .catch(() => { /* the defaults are usable; a failed options call is not a failed scan */ });
    return () => { cancelled = true; };
  }, [open, isAdmin, token, serverFactors]);

  const toggleFactor = (id) => {
    setFactors((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const run = useCallback(async () => {
    const mine = (scanId.current += 1);
    setError('');
    setReport(null);
    setExpanded(null);
    setPaintNote('');
    // CLEARED FIRST and unconditionally. A re-run that finds nothing — or fails — must take the
    // previous answer off the chart rather than leave it standing there looking current. This is
    // the one defect class a modeless panel makes easy: the reader is looking at the canvas, not
    // at the table that no longer matches it.
    if (clearRef.current) clearRef.current();
    try {
      const body = {
        lookbackYears: Number(lookbackYears),
        limit: Number(limit),
        minConfidenceScore: Number(minScore),
        minAnalogAgeDays: Number(minAge),
        enabledFactors: factors,
      };
      // Omitted rather than sent blank: absent means "the market's own current date", and a
      // blank string would be a parse failure the server correctly refuses.
      if (targetDate) body.targetDate = targetDate;
      // The symbol is what adds the forward-outcome column. Sent only when wanted, so a pure
      // astronomy run is distinguishable from one whose symbol had no bars.
      if (useSymbol && symbol) body.symbol = symbol;

      const result = await runScan(token, body, { onState: setState });
      if (mine !== scanId.current) return;
      setReport(result);
      setState('COMPLETE');
      // ⚠ PAINTED AUTOMATICALLY, not behind a second button. The marks on the chart ARE the
      // deliverable — this is a tool for looking at price action around an analog, and a reader
      // who has to find a "draw" control after every run is being charged for the thing they
      // came for. The panel is modeless precisely so the canvas behind it stays visible.
      if (paintRef.current) setPaintNote(paintRef.current(result) || '');
    } catch (e) {
      if (mine !== scanId.current) return;
      setError(e.message || String(e));
      setState('FAILED');
    }
  }, [token, targetDate, lookbackYears, limit, minScore, minAge, factors, useSymbol, symbol]);

  if (!isAdmin) return null;

  const busy = state === 'SUBMITTING' || state === 'RUNNING';
  const summary = outcomeSummary(report);
  const verdict = outcomeVerdict(summary);
  const matches = (report && report.matches) || [];
  // ONE baseline for the whole panel, shared with the chart badges through the same function --
  // a type is relative to the scan, so computing it per row would be the same arithmetic done
  // twenty-five times and a chance for the table and the canvas to disagree.
  const baseline = baselineShares(matches);

  return (
    // MODELESS, like the scanner candidates dialog and for the same reason: the "chart" button
    // frames an analog on the canvas BEHIND this panel, and a backdrop would dim the one thing
    // the gesture exists to show. A reader works down the list, framing each one in turn.
    <Dialog open={!!open} onClose={onClose} maxWidth="xl" fullWidth hideBackdrop
      sx={{ '& .MuiDialog-paper': { maxHeight: '88vh' } }}
      data-testid="planetary-analog-dialog">
      <DialogTitle sx={{ pb: 0.5, fontSize: 16, fontWeight: 700 }}>
        Planetary Analog Scanner
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
          Historical days whose planetary signature resembles the target&apos;s, ranked by how
          improbable the agreement is against the scanned history itself.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        {/* ⚠ FIRST, not in a footnote. The number this panel leads with measures coincidence,
            and a reader who takes it for confidence will act on it. */}
        <Alert severity="warning" sx={{ mb: 1.5, py: 0 }} data-testid="analog-caveat">
          <strong>Research tool — the outcome is unproven.</strong> The score is BITS OF
          IMPROBABILITY: how rare it is for a day to agree with the target this closely. It is
          not a probability that anything happens next. Measured on SPY over 60 analogs the mean
          forward edge against the instrument&apos;s own base rate was +0.17 / +0.27 / +0.06
          points at 5 / 10 / 20 days, t = 0.65 / 0.70 / 0.09 — no edge. Read the OUTCOME row
          below before the bits.
        </Alert>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1 }}>
          <TextField size="small" type="date" label="Target date" value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            sx={{ width: 165 }} InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'analog-target-date' }} />
          <TextField size="small" type="number" label="Lookback (years)" value={lookbackYears}
            onChange={(e) => setLookbackYears(e.target.value)}
            sx={{ width: 140 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 2, max: 100, 'data-testid': 'analog-lookback' }} />
          <TextField size="small" type="number" label="Min age (days)" value={minAge}
            onChange={(e) => setMinAge(e.target.value)}
            sx={{ width: 140 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0, 'data-testid': 'analog-min-age' }} />
          <TextField size="small" type="number" label="Min score (bits)" value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            sx={{ width: 140 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0, 'data-testid': 'analog-min-score' }} />
          <TextField size="small" type="number" label="Limit" value={limit}
            onChange={(e) => setLimit(e.target.value)}
            sx={{ width: 110 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1, max: 500, 'data-testid': 'analog-limit' }} />
          <FormControlLabel
            control={<Checkbox size="small" checked={useSymbol}
              onChange={(e) => setUseSymbol(e.target.checked)}
              inputProps={{ 'data-testid': 'analog-use-symbol' }} />}
            label={<Typography variant="body2">{`Score ${symbol || 'the symbol'} outcomes`}</Typography>}
          />
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {(serverFactors || ALL_FACTORS).map((id) => (
            <Chip key={id} size="small"
              label={FACTOR_LABELS[id] || id}
              color={factors.includes(id) ? 'primary' : 'default'}
              variant={factors.includes(id) ? 'filled' : 'outlined'}
              onClick={() => toggleFactor(id)}
              data-testid={`analog-factor-${id}`} />
          ))}
        </Stack>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
          <Button variant="contained" size="small" onClick={run}
            disabled={busy || !factors.length}
            data-testid="analog-run">
            {busy ? (state === 'SUBMITTING' ? 'Submitting…' : 'Scanning…') : 'Run scan'}
          </Button>
          <Button size="small" onClick={() => {
            if (clearRef.current) clearRef.current();
            setPaintNote('');
          }} data-testid="analog-clear-marks">
            Clear chart marks
          </Button>
          <Button size="small" onClick={() => clearGridCache(token).catch(() => {})}
            disabled={busy} data-testid="analog-clear-cache">
            Clear sky cache
          </Button>
          {!factors.length && (
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              Pick at least one factor — a scan with none has nothing to compare.
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0 }} data-testid="analog-error">{error}</Alert>
        )}

        {report && (
          <>
            <Alert severity="info" sx={{ mb: 1, py: 0 }} data-testid="analog-summary">
              {describeScan(report)}
            </Alert>

            {/* THE CHART LEGEND. Three shapes on a canvas mean nothing without a key, and this
                panel is the only place one can live — the marks sit at the foot of the grid with
                no room for a caption beside them. The RANK is the thing that makes the chart and
                the table one object, so it is stated first. */}
            {paintNote && (
              <Alert severity="success" icon={false} sx={{ mb: 1, py: 0.25 }}
                data-testid="analog-paint">
                <Typography variant="caption" sx={{ display: 'block' }}>{paintNote}</Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  On the chart, at the foot of the price grid, each mark carries a chip reading{' '}
                  <strong>A7 HEL</strong> — the pair and its type. <strong>A<i>n</i></strong> is
                  the FIRST event of pair <i>n</i>; <strong>B<i>n</i></strong> is the second,
                  connected one, which is the target — every pair ends on that single bar, so it
                  carries the range. <strong>◆</strong> an analog with at least one standout
                  factor · <strong>●</strong> one whose score is an accumulation of ordinary
                  agreements · <strong>■</strong> the target. A faint column runs up through the
                  candles. Hover a mark or its chip for the full card.
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {/* The three-letter codes have to be legible from somewhere, and the marks sit
                      at the foot of the grid with no room for a key beside them. The caveat has
                      to travel with them: a reader comparing two scans must know the codes are
                      relative to the set returned. */}
                  <strong>Type</strong> is the family UNUSUALLY STRONG in that match — the one
                  whose share of its raw bits most exceeds that family&apos;s average across
                  this scan, so it says what is different about an analog rather than what is
                  biggest in it (which is the same family every time). It is therefore relative
                  to the analogs returned: <em>changing the limit can change a code.</em>{' '}
                  {Object.entries(FAMILY_CODES)
                    .map(([g, c]) => `${c} ${FACTOR_LABELS[g] || g}`).join(' · ')}.
                </Typography>
              </Alert>
            )}

            {/* THE OUTCOME ROW. Computed in the browser so it moves with the parameters. */}
            {Object.keys(summary).length > 0 && (
              <Box sx={{ mb: 1.5 }} data-testid="analog-outcome">
                <Typography variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: 0.4, color: 'text.secondary' }}>
                  OUTCOME vs this instrument&apos;s all-day baseline
                </Typography>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mt: 0.25 }}>
                  {HORIZONS.map((h) => {
                    const cell = summary[`${h}d`];
                    if (!cell) return null;
                    return (
                      <Typography key={h} variant="body2" data-testid={`analog-edge-${h}`}>
                        <strong>{h}d</strong>{' '}
                        <span style={{
                          color: Math.abs(cell.t) >= 2
                            ? (cell.mean >= 0 ? '#2e7d32' : '#c62828')
                            : 'inherit',
                        }}>
                          {pct(cell.mean)} (t {num(cell.t, 2)}, n {cell.n})
                        </span>
                      </Typography>
                    );
                  })}
                </Stack>
                {verdict && (
                  <Typography variant="caption"
                    sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}
                    data-testid="analog-verdict">
                    {verdict.text}
                  </Typography>
                )}
              </Box>
            )}

            <TableContainer sx={{ maxHeight: '46vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {/* THE KEY BETWEEN THE TWO VIEWS, in the chart's own notation. `A7` is the
                        FIRST event of pair 7 and `B7` is the second — which is the target, the
                        same bar for every pair, so the index lives on the A side. */}
                    <TableCell>Pair</TableCell>
                    <TableCell>Date</TableCell>
                    {/* WHICH FAMILY CARRIED IT. The chart badge is these three letters, so a
                        reader scanning for a KIND of agreement can find its rows here. */}
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Bits</TableCell>
                    <TableCell align="right">Weighted</TableCell>
                    <TableCell align="right">Matched</TableCell>
                    <TableCell align="right">Groups</TableCell>
                    <TableCell align="right">Standouts</TableCell>
                    {HORIZONS.map((h) => (
                      <TableCell key={h} align="right">{`${h}d edge`}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matches.map((m, i) => (
                    <React.Fragment key={m.date}>
                      <TableRow hover sx={{ cursor: 'pointer' }}
                        onClick={() => setExpanded(expanded === m.date ? null : m.date)}
                        data-testid={`analog-row-${m.date}`}>
                        <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {`A${i + 1}`}
                          <span style={{ opacity: 0.5 }}>{` → B${i + 1}`}</span>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {m.date}
                          {/* The chart jump is a SEPARATE control, not the row click: a reader
                              scanning the table should not have the chart yanked under them
                              every time they open a row to read its factors. */}
                          <Button size="small" sx={{ ml: 1, minWidth: 0, py: 0 }}
                            title="Frame this date on the chart behind this panel"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (focusRef.current) focusRef.current(m.date);
                            }}
                            data-testid={`analog-focus-${m.date}`}>
                            chart
                          </Button>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            // ONE derivation, shared with the chart badge and the hover card —
                            // a second would let the table and the canvas label one match two
                            // different ways.
                            const t = matchType(m, baseline);
                            return t ? (
                              <Chip size="small" variant="outlined" label={t.code}
                                title={`${t.label}: ${Math.round(t.share * 100)}% of this `
                                  + `match's raw bits against ${Math.round(t.baseline * 100)}% `
                                  + `across this scan (${t.count} factors, ${num(t.bits)} bits)`} />
                            ) : '—';
                          })()}
                        </TableCell>
                        <TableCell align="right">{num(m.rawBits)}</TableCell>
                        <TableCell align="right">{num(m.weightedScore)}</TableCell>
                        <TableCell align="right">
                          {m.matchedFactors}/{m.applicableFactors}
                        </TableCell>
                        <TableCell align="right">{m.factorGroups}</TableCell>
                        <TableCell align="right">
                          {m.standouts && m.standouts.length
                            ? <Chip size="small" color="secondary" label={m.standouts.length} />
                            : '—'}
                        </TableCell>
                        {HORIZONS.map((h) => {
                          const v = m.outcomeEdge && m.outcomeEdge[`${h}d`];
                          return (
                            <TableCell key={h} align="right" sx={{
                              color: typeof v === 'number'
                                ? (v >= 0 ? 'success.main' : 'error.main') : 'inherit',
                            }}>
                              {pct(v)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8 + HORIZONS.length}
                          sx={{ py: 0, borderBottom: expanded === m.date ? undefined : 'none' }}>
                          <Collapse in={expanded === m.date} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {Object.entries(hitsByGroup(m))
                                  .map(([g, v]) => `${FACTOR_LABELS[g] || g}: ${v.count} (${num(v.bits)} bits)`)
                                  .join(' · ')}
                              </Typography>
                              <Table size="small">
                                <TableBody>
                                  {topHits(m).map((h) => (
                                    <TableRow key={h.factor}>
                                      <TableCell sx={{ border: 0, py: 0.25 }}>
                                        {h.standout && <Chip size="small" color="secondary"
                                          label="standout" sx={{ mr: 1 }} />}
                                        {h.label}
                                      </TableCell>
                                      {/* ⚠ THE WEIGHTED FIGURE, because `hits` arrives sorted
                                          by it. Printing the raw bits here gave a column that
                                          read 5.8 / 5.3 / 3.6 / 3.5 / 3.5 / 4.6 — correctly
                                          ordered by a number that was not on screen. */}
                                      <TableCell align="right" sx={{ border: 0, py: 0.25 }}>
                                        {num(typeof h.weightedBits === 'number'
                                          ? h.weightedBits : h.bits, 2)} wt bits
                                      </TableCell>
                                      <TableCell align="right" sx={{ border: 0, py: 0.25 }}>
                                        {typeof h.separationDeg === 'number'
                                          ? `${num(h.separationDeg, 2)}° apart` : '—'}
                                      </TableCell>
                                      <TableCell align="right" sx={{ border: 0, py: 0.25 }}>
                                        persists {num(h.persistenceDays, 0)}d
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {matches.length === 0 && (
              <Typography variant="body2"
                sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}
                data-testid="analog-empty">
                No analogs cleared the filters. A min score above zero, a long minimum age, or a
                narrow factor set will each do that — and so will a genuinely unprecedented
                configuration, which the summary above names separately.
              </Typography>
            )}

            {report.nonRecurringFactors && report.nonRecurringFactors.length > 0 && (
              <Box sx={{ mt: 1 }} data-testid="analog-nonrecurring">
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  <strong>Never recurred in this window, so excluded from the score:</strong>{' '}
                  {report.nonRecurringFactors.join(', ')}. These are the genuinely unprecedented
                  ones — a pair whose synodic period exceeds the lookback cannot repeat inside
                  it, so scoring it would reward recency rather than analogy. Widen the lookback
                  to make them comparable.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

PlanetaryAnalogDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  isAdmin: PropTypes.bool,
  token: PropTypes.string,
  symbol: PropTypes.string,
  onFocusDate: PropTypes.func,
  /** Hands a finished report to the chart. Returns the status line to show above the table. */
  onPaint: PropTypes.func,
  onClearMarks: PropTypes.func,
};

export default PlanetaryAnalogDialog;
